import { Grid, Paper, Box, TextField } from "@mui/material";
import {
  LinearProgress,
  Button,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Typography } from "@material-ui/core";
import * as React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ReadOnlyTextField from "../../ui/ReadOnlyTextField";
import { store } from "../../../store";
import { NoRowOverlay } from "../../table/NoRowOverlay";
import { getOrder } from "../../../redux/actions/order";
import OrderTable from "../../ui/OrderTable";
import { createShipment, deleteShipment } from "../../../services/shipmentService";
import UserInfo from "../../ui/UserInfo";
import { fetchOrder, updateOrderStatus } from "../../../services/orderService";
import { updateProductionStatus } from "../../../services/productionService";
import { showErrorAlert, showSuccessAlert } from "../../ui/utils/AlertUtils";
import { useTranslation } from "react-i18next";
import AddressContactInfo from "../../ui/AddressContactInfo";
import { MaterialOrderHeader } from "../../ui/BackorderControl";
import {
  MaterialOrderGridGroup,
  materialOrderGridListSx,
} from "../../ui/MaterialOrderGridLayout";

export const ScreenConfirmShippingForm = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const newShipment = location.state?.newShipment;
  const users = useSelector((state) => state.user.users);
  const currentUser = useSelector((state) => state.user.user);

  const user = newShipment?.shippedBy
    ? users.find((u) => u.id === newShipment.shippedBy)
    : currentUser;
  const order = useSelector((state) => state.order.order);
  const centerId = location.state?.centerId;
  const studyId = location.state?.studyId;
  const orderId = location.state?.orderId;
  const [isLoading, setIsLoading] = React.useState(true);
  const centerNumber = location.state?.centerNumber;
  const studyCode = location.state?.studyCode;
  const fullAddress = AddressContactInfo({ order });

  const editShipment = () => {
    navigate("/shipping/new", {
      state: {
        orderId,
        newShipment,
        centerNumber,
        studyCode,
        centerId,
        studyId,
      },
    });
  };
  
  const handleCreateShipment = async () => {
    try {
      let details = `${newShipment?.numPackages} nb of pkg, `;
      newShipment?.packages.forEach((pkg, index) => {
        details += `pkg ${index + 1}: ${pkg.length} l, ${pkg.height} h, ${
          pkg.width
        } w, ${pkg.volumetricWeight} vw; `;
      });
  
      // Create the shipment
      const shipmentResponse = await createShipment(
        orderId,
        newShipment,
        details,
        user
      );
  
      // Get the created shipment's id
      const shipmentId = shipmentResponse.id;
  
      // Associate the productions to the shipment
      for (const key in newShipment.productionsSent) {
        const productionsArray = newShipment.productionsSent[key];
        for (const production of productionsArray) {
          await updateProductionStatus(
            production.id,
            "in transit",
            order?.centerId,
            shipmentId
          );
        }
      }
  
      // Fetch the order and its shipments
      const orderWithShipments = await fetchOrder(orderId);
  
      // Calculate the total shipped quantity
      let totalShippedQuantity = 0;
      for (const shipment of orderWithShipments.shipments) {
        totalShippedQuantity += shipment.shippedQuantity;
      }
  
      // Determine the new order status
      let newOrderStatus;
      if (totalShippedQuantity >= orderWithShipments.totalQuantity) {
        newOrderStatus = "shipped";
      } else {
        newOrderStatus = "shipped 1/2";
      }
  
      // Update the order status
      await updateOrderStatus(orderId, newOrderStatus);
  
      // Delete the draft shipment
      const draftShipment = orderWithShipments.shipments.find(
        (shipment) => shipment.status === "draft"
      );
      if (draftShipment) {
        await deleteShipment(draftShipment.id);
      }
  
      showSuccessAlert("Your shipment was successfully created");
      navigate(`/order`, {
        state: {
          orderId: orderId,
          shipmentId,
          centerId,
          studyId,
          centerNumber,
          studyCode,
        },
      });
    } catch (error) {
      showErrorAlert("An error occurred while creating the shipment");
      console.error("An error occurred while creating the order:", error);
      navigate(`/orders/`);
    }
  };

  const materialsTypeMaterial = order.Supplies.filter(
    (supply) => supply.Material.type === "material"
  );
  const materialsTypeSupply = order.Supplies.filter(
    (supply) => supply.Material.type === "supply"
  );
  const materialsTypeReagent = order.Supplies.filter(
    (supply) => supply.Material.type === "reagent"
  );
  const materialsTypeDocuments = order.Supplies.filter(
    (supply) => supply.Material.type === "document"
  );

  const CustomDataGrid = ({ rows, headerName }) => {
    const columns = [
      {
        field: "materialName",
        headerName: headerName,
        flex: 0.2,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueGetter: (params) => params.row.Material.name,
      },
      {
        field: "quantity",
        headerName: t("neededQuantity"),
        flex: 0.1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: "shippingQuantity",
        headerName: t("sentQuantity"),
        flex: 0.1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          return (
            <div>
              {
                (newShipment?.productionsSent[params.row.Material.id] || [])
                  .length
              }
            </div>
          );
        },
      },
      {
        field: "productionsSent",
        headerName: t("selectedProductions"),
        flex: 0.2,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const productionsSent =
            newShipment?.productionsSent[params.row.Material.id] || [];
          const references = productionsSent.map(
            (production) =>
              `(${production.batchNumber}) ${production.reference} ${production.comment}`
          );
          return (
            <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
              {references.join(", ")}
            </div>
          );
        },
      },
    ];

    if (!rows || rows.length === 0) {
      return null;
    }

    return (
      <MaterialOrderGridGroup>
        <DataGrid
          slots={{
            noRowsOverlay: NoRowOverlay,
            loadingOverlay: LinearProgress,
          }}
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 30 },
            },
          }}
          pageSizeOptions={[30, 50]}
          disableSelectionOnClick
          hideFooter
          autoHeight
        />
      </MaterialOrderGridGroup>
    );
  };

  // fetch order
  React.useEffect(() => {
    store.dispatch(getOrder(orderId)).then(() => setIsLoading(false));
  }, [orderId]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Grid item xs={12}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ width: "100%", height: "auto" }}>
          <OrderTable
            order={order}
            studyCode={studyCode}
            centerNumber={centerNumber}
          />
          <ReadOnlyTextField
            title={t("shippingAddress").toUpperCase()}
            value={fullAddress}
            multiline
            maxRows={4}
          />

          <Box sx={{ p: 2 }}>
            <Box sx={materialOrderGridListSx}>
              <TextField
                value={t("material").toUpperCase()}
                InputProps={{
                  readOnly: true,
                }}
                inputProps={{
                  style: { textAlign: "center" },
                }}
                fullWidth
              />
              <CustomDataGrid
                rows={materialsTypeMaterial}
                headerName={t("material").toUpperCase()}
              />
              <CustomDataGrid
                rows={materialsTypeSupply}
                headerName={t("supply").toUpperCase()}
              />
              <CustomDataGrid
                rows={materialsTypeReagent}
                headerName={t("reagent").toUpperCase()}
              />
              <CustomDataGrid
                rows={materialsTypeDocuments}
                headerName={t("document").toUpperCase()}
              />
            </Box>
          </Box>
        </div>

        <Box sx={{ p: 2 }}>
          <MaterialOrderHeader
            backorderLabel={t("onBackorder")}
            showBackorder={Boolean(newShipment?.onBackorder)}
            title={t("shippingInformation").toUpperCase()}
          />

          <Grid container spacing={2} style={{ marginTop: "12px" }}>
            <Grid item xs={4}>
              <TextField
                label={t("preparationDate")}
                type="date"
                name="preparationDate"
                value={newShipment?.preparationDate.split("T")[0] }
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={t("sendingDate")}
                type="date"
                name="sendingDate"
                value={newShipment?.sendingDate.split("T")[0] }
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={t("carrierOrderDate")}
                type="date"
                name="carrierOrderDate"
                value={newShipment?.carrierOrderDate.split("T")[0] }
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={t("carrier")}
                fullWidth
                name="carrier"
                value={newShipment?.carrier}
                disabled
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={t("airwaybill")}
                name="packingSlip"
                value={newShipment?.packingSlip}
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={t("numberOfPackages")}
                type="number"
                name="numPackages"
                value={newShipment?.numPackages}
                fullWidth
                disabled
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            alignItems="center"
            style={{ marginTop: "8px" }}
          >
            {Array.from({ length: newShipment?.numPackages }, (_, index) => (
              <React.Fragment key={index}>
                <Grid item xs={3}>
                  <Typography variant="body1">
                    {t("package")} {index + 1}:
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label={t("length")}
                    type="number"
                    value={newShipment?.packages[index]?.length || ""}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">cm</InputAdornment>
                      ),
                    }}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label={t("height")}
                    type="number"
                    value={newShipment?.packages[index]?.height || ""}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">cm</InputAdornment>
                      ),
                    }}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label={t("width")}
                    type="number"
                    value={newShipment?.packages[index]?.width || ""}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">cm</InputAdornment>
                      ),
                    }}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label={t("volumetricWeight")}
                    value={
                      (newShipment?.packages[index] &&
                        newShipment?.packages[index].volumetricWeight) ||
                      0
                    }
                    type="number"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">kg</InputAdornment>
                      ),
                    }}
                    fullWidth
                    disabled
                  />
                </Grid>
              </React.Fragment>
            ))}
          </Grid>

          {newShipment?.comment && (
            <TextField
              id="comment"
              name="comment"
              label="Comment"
              multiline
              rows={3}
              fullWidth
              sx={{ m: 2 }}
              value={newShipment?.comment}
              disabled
            />
          )}
          <UserInfo
            title={t("personInChargeOfShipping").toUpperCase()}
            user={user}
            date={new Date().toLocaleDateString()}
          />
        </Box>
        <Grid
          item
          xs={12}
          style={{ display: "flex", justifyContent: "space-around" }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={editShipment}
            style={{ padding: "16px 88px", margin: "16px" }}
          >
            {t("editShippingInfo")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateShipment}
            style={{ padding: "16px 88px", margin: "16px" }}
          >
            {t("confirmShippingInfo")}
          </Button>
        </Grid>
      </Paper>
    </Grid>
  );
};
